import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { crewMembersQuery } from "@/lib/queries/productionQueries";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { compareSetTimes } from "@/lib/utils";
import { getStageLabel } from "@/lib/stageLabel";
import { festivalDayIso, findCoAssignees, findLineupSlotForAssignment } from "@produce/shared";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { CrewScheduleTimeline, type TimelineItem } from "./CrewScheduleTimeline";

interface CrewNextUpSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crewMemberId: string;
  briefingId: string;
  selectedDay: string;
}

export function CrewNextUpSheet({
  open,
  onOpenChange,
  crewMemberId,
  briefingId,
  selectedDay,
}: CrewNextUpSheetProps) {
  // This crew member's own assignments for the day (the schedule we render).
  const { data: assignments, isLoading } = useQuery({
    queryKey: ["crew-assignments", crewMemberId, briefingId, selectedDay],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_assignments")
        .select("*, briefing_stages(label)")
        .eq("crew_member_id", crewMemberId)
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay)
        .eq("is_active", true)
        .order("filming_start", { ascending: true });

      if (error) throw error;
      return data?.sort((a, b) => compareSetTimes(a.filming_start, b.filming_start));
    },
    enabled: open && !!crewMemberId && !!briefingId && !!selectedDay,
  });

  const { data: crewMember } = useQuery({
    queryKey: ["crew-member-detail", crewMemberId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("id", crewMemberId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: open && !!crewMemberId,
  });

  // Set times for the day (lineup), keyed to assignments by lineup_id FK
  // (post-migration 20260504240200) with the legacy artist+stage fallback.
  const { data: lineupData } = useQuery({
    queryKey: ["lineup-for-assignments", briefingId, selectedDay],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lineup")
        .select("*, briefing_stages(label)")
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay);

      if (error) throw error;
      return data;
    },
    enabled: open && !!briefingId && !!selectedDay,
  });

  // Every assignment in the briefing for the day + the crew roster — needed
  // to derive "also filming this" (which other shooters cover the same set).
  const { data: allAssignments } = useQuery({
    queryKey: ["briefing-assignments", briefingId, selectedDay],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_assignments")
        .select("*")
        .eq("briefing_id", briefingId)
        .eq("event_date", selectedDay)
        .eq("is_active", true);

      if (error) throw error;
      return data;
    },
    enabled: open && !!briefingId && !!selectedDay,
  });

  const { data: crewMembers } = useQuery({
    // Shared definition: this key is also read by the production overview and
    // the schedule export, which need it ordered by name. A second queryFn
    // here would decide their row order for them.
    ...crewMembersQuery(briefingId),
    enabled: open && !!briefingId,
  });

  // Re-render every 30s so the live "now" marker keeps pace with real time.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setNowTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, [open]);

  // Live tracking is a producer view, so it follows the PRODUCER's own 12h/24h
  // schedule setting — like the schedule creator and the read-only timeline it
  // sits next to — and NOT the crew member's crew_settings.time_format, which
  // governs what they see on their own device.
  const formatTime = useProducerTimeFormatter();

  const currentTime = format(new Date(), "HH:mm");
  const isToday = selectedDay === festivalDayIso();

  // Exclude assistant mirror-copies so an assistant isn't double-listed as a
  // separate shooter on the same set.
  const realAssignments = useMemo(
    () =>
      (allAssignments ?? []).filter(
        (a) => !(a as { mirrored_from_id?: string | null }).mirrored_from_id,
      ),
    [allAssignments],
  );

  // Build the timeline view-models once per data change — each item runs
  // findCoAssignees (scans every briefing assignment) + a lineup lookup, so
  // we must not redo it on every render (the 30s "now" tick re-renders us).
  const items: TimelineItem[] = useMemo(
    () =>
      (assignments ?? []).map((a) => {
        const isArtist = (a.assignment_type ?? "artist") === "artist";
        const lineupInfo = isArtist ? findLineupSlotForAssignment(a, lineupData ?? []) : null;
        const coAssignees = findCoAssignees(a, realAssignments, crewMembers ?? []).map((c) => ({
          name: c.name,
          start: c.filmingStart,
          end: c.filmingEnd,
        }));
        return {
          id: a.id,
          filmingStart: a.filming_start,
          filmingEnd: a.filming_end,
          displayName:
            (isArtist ? a.artist_name || a.subject : a.subject || a.artist_name) || "Untitled",
          stageLabel: getStageLabel(a),
          isBreak: (a.assignment_type ?? "") === "break",
          setStart: lineupInfo?.set_start ?? null,
          setEnd: lineupInfo?.set_end ?? null,
          note: a.notes ?? null,
          coAssignees,
        };
      }),
    [assignments, lineupData, realAssignments, crewMembers],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-[400px] flex-col sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Full Schedule</SheetTitle>
          <p className="text-sm text-muted-foreground">
            {crewMember?.name} - {crewMember?.function}
          </p>
        </SheetHeader>

        <ScrollArea className="mt-6 flex-1">
          <div className="pb-6 pr-2">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : items.length > 0 ? (
              <CrewScheduleTimeline
                items={items}
                formatTime={formatTime}
                showCurrentTime={isToday}
                currentTime={currentTime}
                fallbackHour={new Date().getHours()}
              />
            ) : (
              <div className="rounded-xl border border-border p-6 text-center">
                <p className="text-sm text-muted-foreground">No assignments for this day</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
